import Clarity from "@microsoft/clarity";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate } from "react-router-dom";
import { CLARITY_PROJECT_ID } from "../../../project_config";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import { useAdminGate } from "../../../utils/useAdminGate";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import Footer from "../../common/footer/Footer";
import Header from "../../common/header/Header";
import Paginator from "../../common/paginator/Paginator";
import DashboardSidebar from "../DashboardSidebar";

const emptyForm = { name: "", study: "", location: "", confirm_state: "waiting" };
const statusOptions = [
	{ value: "waiting", label: "در انتظار بررسی" },
	{ value: "confirmed", label: "تأیید شده" },
	{ value: "refused", label: "رد شده" },
];

const inputClassName = "w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 font-Vazir-400 text-[14px] text-darkBlue outline-none transition focus:border-darkBlue/40 focus:ring-2 focus:ring-darkBlue/10";

export default function AdminDoctors() {
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
	const [doctors, setDoctors] = useState([]);
	const [form, setForm] = useState(emptyForm);
	const [editingId, setEditingId] = useState(null);
	const [listQuery, setListQuery] = useState("");
	const [appliedQuery, setAppliedQuery] = useState("");
	const [page, setPage] = useState(1);
	const [maxPage, setMaxPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [notice, setNotice] = useState("");
	const listRef = useRef(null);
	const { ready, isAdmin } = useAdminGate();

	useEffect(() => {
		Clarity.init(CLARITY_PROJECT_ID);
		const handleResize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const fetchDoctors = useCallback((currentPage = 1, currentQuery = "") => {
		setIsLoading(true);
		setErrorMessage("");
		return api.doctorAdminList({
			token: LoginHelper.getToken(),
			query: currentQuery,
			page: currentPage,
		}).then((result) => {
			setDoctors(result.doctors || []);
			setPage(result.page || 1);
			setMaxPage(result.max_page || 1);
			setTotalCount(result.total_count || 0);
			return result;
		}).catch((error) => {
			setErrorMessage(error?.data?.message || "دریافت فهرست دکترها ناموفق بود");
			return null;
		}).finally(() => setIsLoading(false));
	}, []);

	useEffect(() => {
		if (isAdmin) fetchDoctors(page, appliedQuery);
	}, [isAdmin, page, appliedQuery, fetchDoctors]);

	function applySearch(event) {
		event.preventDefault();
		const nextQuery = listQuery.trim();
		if (nextQuery !== appliedQuery) {
			setAppliedQuery(nextQuery);
			setPage(1);
			return;
		}
		fetchDoctors(page, appliedQuery);
	}

	function startEditing(doctor) {
		setEditingId(doctor.id);
		setForm({
			name: doctor.name || "",
			study: doctor.study || "",
			location: doctor.location || "",
			confirm_state: doctor.confirm_state || "waiting",
		});
		setNotice("");
		setErrorMessage("");
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	function resetForm() {
		setEditingId(null);
		setForm(emptyForm);
	}

	function saveDoctor(event) {
		event.preventDefault();
		if (isSaving) return;
		setIsSaving(true);
		setErrorMessage("");
		setNotice("");
		api.doctorAdminSave({
			token: LoginHelper.getToken(),
			...(editingId ? { doctor_id: editingId } : {}),
			name: form.name.trim(),
			study: form.study.trim(),
			location: form.location.trim(),
			confirm_state: form.confirm_state,
		}).then(() => {
			setNotice(editingId ? "اطلاعات دکتر به‌روزرسانی شد." : "دکتر جدید با موفقیت ساخته شد.");
			resetForm();
			return fetchDoctors(page, appliedQuery);
		}).catch((error) => {
			setErrorMessage(error?.data?.message || "ذخیره اطلاعات دکتر ناموفق بود");
		}).finally(() => setIsSaving(false));
	}

	function removeDoctor(doctor) {
		if (!window.confirm(`پروفایل «${doctor.name}» حذف شود؟ این عملیات قابل بازگشت نیست.`)) return;
		setErrorMessage("");
		setNotice("");
		api.doctorAdminRemove({ token: LoginHelper.getToken(), doctor_id: doctor.id })
			.then(() => {
				setNotice("پروفایل دکتر حذف شد.");
				if (editingId === doctor.id) resetForm();
				return fetchDoctors(page, appliedQuery);
			})
			.then((result) => {
				if (result && result.doctors.length === 0 && page > 1) setPage(page - 1);
			})
			.catch((error) => {
				setErrorMessage(error?.data?.message || "حذف دکتر ناموفق بود");
			});
	}

	if (!ready) return null;
	if (!isAdmin) return <Navigate to="/login/" replace />;

	const statusLabel = (value) => statusOptions.find((option) => option.value === value)?.label || value;
	const listSummary = maxPage > 1
		? `${totalCount.toLocaleString("fa-IR")} دکتر · صفحه ${page.toLocaleString("fa-IR")} از ${maxPage.toLocaleString("fa-IR")}`
		: `${totalCount.toLocaleString("fa-IR")} دکتر`;

	return (
		<>
			<Helmet>
				<title>مدیریت دکترها | همراهتیم</title>
				<meta name="description" content="مدیریت دکترهای همراهتیم" />
			</Helmet>
			<div className="flex min-h-screen flex-col justify-between bg-[#F0F6FC]">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div className="mx-auto max-w-[1200px] px-4">
							<Breadcrumb linkDataList={[
								{ label: "خانه", href: "/" },
								{ label: "داشبورد مدیریت", href: "/dashboard/" },
								{ label: "مدیریت دکترها", href: "/dashboard/doctors/" },
							]} />
						</div>
					</div>
					<main className={`mx-auto flex w-full max-w-[1200px] gap-5 py-8 ${isDesktop ? "flex-row px-4" : "flex-col p-[10px]"}`}>
						<DashboardSidebar isDesktop={isDesktop} />
						<section className="flex min-w-0 flex-1 flex-col gap-5">
							<div className="rounded-[12px] border border-[#E2E8F0] bg-white p-5 sm:p-4">
								<h1 className="font-Vazir-800 text-[22px] text-darkBlue">مدیریت دکترها</h1>
								<p className="mt-1 font-Vazir-400 text-[13px] text-darkBlue/55">ساخت، ویرایش و مدیریت پروفایل دکترها</p>
								<form onSubmit={saveDoctor} className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-1">
									<label className="flex flex-col gap-1.5 font-Vazir-500 text-[13px] text-darkBlue">
										نام و نام خانوادگی <input required maxLength={100} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className={inputClassName} placeholder="نام دکتر" />
									</label>
									<label className="flex flex-col gap-1.5 font-Vazir-500 text-[13px] text-darkBlue">
										رشته تحصیلی <input maxLength={500} value={form.study} onChange={(event) => setForm({ ...form, study: event.target.value })} className={inputClassName} placeholder="مثلاً روانشناسی بالینی" />
									</label>
									<label className="flex flex-col gap-1.5 font-Vazir-500 text-[13px] text-darkBlue">
										موقعیت مکانی <input maxLength={100} value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} className={inputClassName} placeholder="شهر یا محل فعالیت" />
									</label>
									<label className="flex flex-col gap-1.5 font-Vazir-500 text-[13px] text-darkBlue">
										وضعیت تأیید <select value={form.confirm_state} onChange={(event) => setForm({ ...form, confirm_state: event.target.value })} className={inputClassName}>
											{statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
										</select>
									</label>
									<div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1">
										<button type="submit" disabled={isSaving} className="rounded-xl bg-cta px-5 py-3 font-Vazir-700 text-[14px] text-white transition hover:bg-cta/90 disabled:opacity-60">
											{isSaving ? "در حال ذخیره…" : editingId ? "ذخیره تغییرات" : "ساخت دکتر"}
										</button>
										{editingId && <button type="button" onClick={resetForm} className="rounded-xl border border-darkBlue/20 px-5 py-3 font-Vazir-600 text-[14px] text-darkBlue">انصراف از ویرایش</button>}
									</div>
								</form>
								{!editingId && <p className="mt-3 font-Vazir-400 text-[12px] text-darkBlue/50">برای دکتر جدید، شماره تستی با پیشوند 0009 به‌صورت خودکار ساخته می‌شود.</p>}
								{notice && <p role="status" className="mt-3 rounded-xl bg-green-50 px-4 py-3 font-Vazir-500 text-[13px] text-green-800">{notice}</p>}
								{errorMessage && <p role="alert" className="mt-3 rounded-xl bg-red-50 px-4 py-3 font-Vazir-500 text-[13px] text-red-700">{errorMessage}</p>}
							</div>

							<div ref={listRef} className="rounded-[12px] border border-[#E2E8F0] bg-white p-5 sm:p-4">
								<div className="flex flex-col gap-4 sm:items-stretch md:flex-row md:items-end md:justify-between">
									<div>
										<h2 className="font-Vazir-800 text-[19px] text-darkBlue">فهرست دکترها</h2>
										<p className="mt-1 font-Vazir-400 text-[13px] text-darkBlue/55">{listSummary}{appliedQuery ? ` · جستجو: «${appliedQuery}»` : ""}</p>
									</div>
									<form onSubmit={applySearch} className="flex flex-col gap-2 sm:flex-row">
										<input type="search" value={listQuery} onChange={(event) => setListQuery(event.target.value)} placeholder="جستجو بر اساس نام، شماره، رشته یا شهر" className={`${inputClassName} sm:min-w-[280px]`} />
										<button type="submit" disabled={isLoading} className="rounded-xl border border-darkBlue/20 px-5 py-3 font-Vazir-600 text-[13px] text-darkBlue disabled:opacity-60">جستجو</button>
									</form>
								</div>
								<div className={`mt-4 grid gap-3 transition-opacity ${isLoading ? "opacity-60" : ""}`}>
									{doctors.map((doctor) => (
										<article key={doctor.id} className="flex flex-col gap-3 rounded-xl border border-darkBlue/10 p-4 sm:flex-row sm:items-center sm:justify-between">
											<div className="min-w-0">
												<h3 className="font-Vazir-700 text-[15px] text-darkBlue">{doctor.name}</h3>
												<p className="mt-1 font-Vazir-400 text-[12px] text-darkBlue/65" dir="ltr">{doctor.phone_number}</p>
												<div className="mt-2 flex flex-wrap gap-2">
													{doctor.study && <span className="rounded-lg bg-darkBlue/5 px-2.5 py-1 font-Vazir-500 text-[12px] text-darkBlue/70">{doctor.study}</span>}
													{doctor.location && <span className="rounded-lg bg-darkBlue/5 px-2.5 py-1 font-Vazir-500 text-[12px] text-darkBlue/70">{doctor.location}</span>}
													<span className="rounded-lg bg-lightCta px-2.5 py-1 font-Vazir-600 text-[12px] text-darkBlue">{statusLabel(doctor.confirm_state)}</span>
												</div>
											</div>
											<div className="flex shrink-0 gap-2">
												<button type="button" onClick={() => startEditing(doctor)} className="rounded-lg bg-lightCta px-4 py-2 font-Vazir-600 text-[13px] text-darkBlue">ویرایش</button>
												<button type="button" onClick={() => removeDoctor(doctor)} className="rounded-lg border border-red-200 px-4 py-2 font-Vazir-600 text-[13px] text-red-600 hover:bg-red-50">حذف</button>
											</div>
										</article>
									))}
									{!isLoading && doctors.length === 0 && <p className="py-8 text-center font-Vazir-400 text-[14px] text-midGray">{appliedQuery ? "دکتری با این جستجو پیدا نشد." : "هنوز دکتری ثبت نشده است."}</p>}
								</div>
								{maxPage > 1 && <div className="mt-6 flex justify-center"><Paginator currentPage={page} maxPageNum={maxPage} isLoading={isLoading} onChange={setPage} scrollToTop={false} scrollAnchorRef={listRef} /></div>}
							</div>
						</section>
					</main>
				</div>
				<Footer isDesktop={isDesktop} />
			</div>
		</>
	);
}
